import { autoinject, observable } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { Api, apiCall } from 'app/shared/lib/api/api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { searchInOrder, searchAny } from 'app/shared/lib/regexp';
import { IHtmlValue, markText } from 'app/shared/lib/formatting/html-utilities';
import { localSort } from 'app/shared/lib/sorting';
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class ResourcesView extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected categories: Core.ResourceCategory[] = [];

    @observable
    protected filterText: string = '';
    protected searchTerms: string[] = [];
    protected resources: Core.Resource[] = [];

    protected minSearchLength: number = 3;
    protected maxSearchLength: number = 200;

    private defaultSorting: Sorting = new Sorting();

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(private readonly api: Api,
        ...rest: any[]) {
        super(...rest);

        this.defaultSorting.entries = [{
            reference: 'description',
            direction: SortDirection.Ascending
        }];
    }

    protected bind(): void {
        this.loadResources();
    }

    ////****************** Events ***************************************************////



    ////****************** Functions ************************************************////
    protected validFilterText(): boolean {
        return this.filterText.trim().length >= this.minSearchLength;
    }

    protected filterTextChanged(): void {
        // trim and truncate to max search length (to prevent DOS)
        const search = this.filterText.trim().substring(0, this.maxSearchLength);

        // make sure minimum search length criteria is met
        if(search && search.length >= this.minSearchLength) {
            this.searchTerms = search.split(/\s+/);
            // this regexp is not global since we only need to find the first match
            const searchRegExp = searchInOrder(this.searchTerms, 'i');

            const results: Core.Resource[] = [];
            this.search(results, this.categories, searchRegExp);
            this.resources = results;
            localSort(this.resources, this.defaultSorting);
        }
        else {
            this.searchTerms = [];
            this.resources = [];
        }
    }

    private search(results: Core.Resource[], allCategories: Core.ResourceCategory[], searchRegExp: RegExp): void {
        for(const category of allCategories) {
            if(!category.resources) { return; }
            for(const item of category.resources) {
                if((item.summary && item.summary.match(searchRegExp)) || (item.description && item.description.match(searchRegExp))) {
                    results.push(item);
                }
            }
        }
    }

    protected mark(text: string, searchTerms: string[]): IHtmlValue | null {
        // use a new RegExp each time since RegExp objects for global searches have state
        // this regex is global since we want to mark all occurrences
        const searchRegExp = searchAny(searchTerms, 'ig');
        return markText(text, searchRegExp, 'mark', 'ess-search');
    }


    ////****************** Service Calls ********************************************////

    @apiCall
    private async loadResources(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = { all: true };
        const includes = [
            'resources'
        ];
        const fields = [
            'description',
            'resources.description',
            'resources.href',
            'resources.summary'
        ];

        try {
            this.state.beginLoad();
            const query = this.api
                .query('resources/categories')
                .applyStandardLoadOptions(options)
                .include(...includes)
                .field(...fields);

            const response = await this.api.get(query);
            this.categories = response.results;
            this.categories.forEach(category => {
                if(category.resources) localSort(category.resources, this.defaultSorting);
            });
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }

    }
}
